import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useReceivablesOutlook from '@/hooks/useReceivablesOutlook';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeInvoicesService, { FxOpenReceivable } from '@/services/financeInvoices';
import financeQuickBooksService from '@/services/financeQuickBooks';
import { FetchApiIds } from '@/types';
import { FxInvoiceStatus } from '@/types/api/finance';
import FinanceXInvoicesComponent from './FinanceXInvoices.component';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateFilter = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

function lastDayOfMonth(year: number, month: number): string {
  return new Date(year, month + 1, 0).toISOString().split('T')[0];
}

function capAtToday(endDate: string): string {
  const today = new Date().toISOString().split('T')[0];
  return endDate > today ? today : endDate;
}

function getYearRange(year: number, cap = false): { startDate: string; endDate: string } {
  const endDate = `${year}-12-31`;
  return { startDate: `${year}-01-01`, endDate: cap ? capAtToday(endDate) : endDate };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const endDate = lastDayOfMonth(target.getFullYear(), target.getMonth());
  return { startDate: target.toISOString().split('T')[0], endDate: capAtToday(endDate) };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const endDate = lastDayOfMonth(now.getFullYear(), q * 3 + 2);
  return { startDate: start.toISOString().split('T')[0], endDate: capAtToday(endDate) };
}

const FinanceXInvoicesContainer = function (): JSX.Element {
  const currentYear = new Date().getFullYear();
  const orgId = getActiveOrgId();
  const queryClient = useQueryClient();
  const toast = useToastContext();

  const [statusFilter, setStatusFilter] = useState<FxInvoiceStatus | undefined>(undefined);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ preset: 'this_year', ...getYearRange(currentYear, true) });
  const [isSyncing, setIsSyncing] = useState(false);

  // Handle QB OAuth callback redirect (user returns from QB auth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('qb_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      toast.setMessage('QuickBooks connected! Click Sync QB to fetch invoices.');
      toast.setSeverity('success');
      toast.showToast();
    } else if (params.get('qb_error')) {
      window.history.replaceState({}, '', window.location.pathname);
      toast.showErrorFeedback('QuickBooks connection failed. Please try again.');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePresetChange = useCallback((preset: DatePreset) => {
    switch (preset) {
      case 'this_month': setDateFilter({ preset, ...getMonthRange(0) }); break;
      case 'last_month': setDateFilter({ preset, ...getMonthRange(-1) }); break;
      case 'this_quarter': setDateFilter({ preset, ...getQuarterRange() }); break;
      case 'this_year': setDateFilter({ preset, ...getYearRange(currentYear, true) }); break;
      case 'last_year': setDateFilter({ preset, ...getYearRange(currentYear - 1) }); break;
      default: setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, [currentYear]);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate: capAtToday(endDate) });
  }, []);

  const { data: invoicesData, isLoading } = useApiFetch(
    [FetchApiIds.FX_INVOICES, orgId, statusFilter, dateFilter.startDate, dateFilter.endDate],
    () => financeInvoicesService.getAll({
      page: 1, take: 500, status: statusFilter,
      startDate: dateFilter.startDate, endDate: dateFilter.endDate,
    })
  );

  const { data: summary } = useApiFetch(
    [FetchApiIds.FX_INVOICES, orgId, 'summary', dateFilter.startDate, dateFilter.endDate],
    () => financeInvoicesService.getSummary({ startDate: dateFilter.startDate, endDate: dateFilter.endDate })
  );

  const { data: byClient } = useApiFetch([FetchApiIds.FX_INVOICES, orgId, 'by-client'], () =>
    financeInvoicesService.getByClient()
  );

  const { data: aging } = useApiFetch([FetchApiIds.FX_INVOICES, orgId, 'aging'], () =>
    financeInvoicesService.getAgingReport()
  );

  // Deliberately unfiltered by dateFilter — the cash outlook covers every unpaid
  // invoice, whenever it was issued, because due dates run into the future.
  // useSeoFetch (not useApiFetch) because this service returns plain data
  // rather than an AxiosResponse.
  const { data: openReceivables } = useSeoFetch<FxOpenReceivable[]>(
    [FetchApiIds.FX_INVOICES, orgId, 'open-receivables'],
    () => financeInvoicesService.getOpenReceivables(),
    { retry: false }
  );

  const outlook = useReceivablesOutlook(openReceivables ?? []);

  const handleStatusFilter = (status: FxInvoiceStatus | undefined): void => {
    setStatusFilter(status);
  };

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const connections = await financeQuickBooksService.getConnections();
      if (connections.length === 0) {
        // No connection — redirect to QB OAuth (same approach as Integrations page)
        const { authUrl } = await financeQuickBooksService.getAuthUrl('/financex/invoices');
        window.location.href = authUrl;
        return;
      }
      const result = await financeQuickBooksService.syncConnection(connections[0].id, {
        syncType: 'invoices',
      });
      const count = result.created + result.updated;
      toast.setMessage(`Synced ${count} invoice${count !== 1 ? 's' : ''} from QuickBooks.`);
      toast.setSeverity('success');
      toast.showToast();
      queryClient.invalidateQueries(FetchApiIds.FX_INVOICES);
    } catch (err: any) {
      // Show the real reason when we have one. A generic message here sent the
      // user hunting through the QuickBooks connection for what was actually an
      // expired login session.
      toast.showErrorFeedback(err?.message || 'Failed to sync invoices from QuickBooks.');
    } finally {
      setIsSyncing(false);
    }
  }, [queryClient, toast]);

  const invoices = invoicesData?.data ?? [];

  return (
    <FinanceXInvoicesComponent
      invoices={invoices}
      isLoading={isLoading}
      statusFilter={statusFilter}
      onStatusFilter={handleStatusFilter}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
      onSync={handleSync}
      isSyncing={isSyncing}
      summary={
        summary ?? {
          totalInvoiced: 0,
          totalPaid: 0,
          totalOutstanding: 0,
          totalOverdue: 0,
          countByStatus: { paid: 0, partial: 0, open: 0, overdue: 0 },
        }
      }
      byClient={byClient ?? []}
      aging={aging ?? []}
      outlook={outlook}
    />
  );
};

export default FinanceXInvoicesContainer;
